import {Box, IconButton, Link} from '@primer/react'
import {HeaderMenu, type HeaderMenuProps} from './HeaderMenu'
import {LABELS, TEST_IDS} from '../../constants'
import {PageHeader, type TitleProps} from '@primer/react/experimental'

import type {HeaderMenu$key} from './__generated__/HeaderMenu.graphql'
import type {HeaderTitleProps} from './HeaderTitle'
import type {OptionConfig} from '../OptionConfig'
import {PencilIcon} from '@primer/octicons-react'
import {SafeHTMLBox} from '@github-ui/safe-html'
import {issueHovercardPath} from '@github-ui/paths'
import {useMemo} from 'react'

type Props = {
  menuData: HeaderMenu$key
  repository: {isPrivate: boolean; ownerAvatarUrl: string; ownerType: string}
  optionConfig?: OptionConfig
  backButtonText?: string
  showRepositoryPill?: boolean
  showCreateIssueButton?: boolean
  titleAs?: TitleProps['as']
  setIsIssueTitleEditActive: (isActive: boolean) => void
  editInProgress: boolean
  number: number
  repoId: string
  viewerCanUpdate: boolean
} & HeaderMenuProps &
  HeaderTitleProps

export function HeaderViewer({
  titleHTML,
  url,
  backButtonText,
  titleAs = 'h1',
  setIsIssueTitleEditActive,
  editInProgress,
  number,
  repoId,
  repoName,
  repoOwner,
  viewerCanUpdate,
  ...headerMenuProps
}: Props) {
  const headerMenu = (
    <HeaderMenu {...headerMenuProps} scopedRepository={{id: repoId, name: repoName, owner: repoOwner}} />
  )

  const editIssueTitleButton = useMemo(
    () =>
      viewerCanUpdate && !editInProgress ? (
        <IconButton
          data-testid={TEST_IDS.editIssueTitleButton}
          variant="invisible"
          aria-label={LABELS.editTitle}
          icon={PencilIcon}
          size="small"
          onClick={() => {
            setIsIssueTitleEditActive(true)
          }}
          sx={{display: 'inline', order: 3}}
        />
      ) : null,
    [editInProgress, setIsIssueTitleEditActive, viewerCanUpdate],
  )

  return (
    <Box aria-label="Header" role={'region'} data-testid={TEST_IDS.issueHeader}>
      <PageHeader
        sx={{
          gap: 2,
          px: 1,
        }}
      >
        <PageHeader.ContextArea>
          <Box
            onClick={headerMenuProps.navigateBack}
            sx={{
              cursor: 'pointer',
            }}
          >
            <PageHeader.ParentLink>{backButtonText}</PageHeader.ParentLink>
          </Box>
          <PageHeader.ContextAreaActions>{headerMenu}</PageHeader.ContextAreaActions>
        </PageHeader.ContextArea>

        <PageHeader.TitleArea sx={{pt: 2}}>
          <PageHeader.Title as={titleAs} sx={{display: 'inline', lineHeight: '1.3', fontSize: 4}}>
            <SafeHTMLBox
              as="bdi"
              sx={{display: 'inline', wordBreak: 'break-word', mr: 1}}
              className="markdown-title"
              html={titleHTML}
              data-testid={TEST_IDS.issueTitle}
            />
            <Box sx={{display: 'inline-flex', whiteSpace: 'nowrap', alignItems: 'center', gap: 1}}>
              <Link
                href={url}
                target="_blank"
                data-hovercard-url={issueHovercardPath({
                  owner: repoOwner,
                  repo: repoName,
                  issueNumber: number,
                })}
                sx={{color: 'fg.muted', fontWeight: 'normal', display: 'inline'}}
              >
                {LABELS.issueNumber(number)}
              </Link>
            </Box>
          </PageHeader.Title>
          {editIssueTitleButton}
          <PageHeader.Actions sx={{display: ['none', 'none', 'flex']}}>{headerMenu}</PageHeader.Actions>
        </PageHeader.TitleArea>
      </PageHeader>
    </Box>
  )
}

try{ HeaderViewer.displayName ||= 'HeaderViewer' } catch {}